<template>
  <component
    :is="iconComponent"
    :size="size"
    :stroke-width="strokeWidth"
    :class="['app-icon', { 'app-icon-spin': spin }]"
  />
</template>

<script>
import {
  Apple, ArrowLeft, ArrowRight, SkipBack, SkipForward, Bell,
  Radio, CalendarDays, Camera, BarChart2, PieChart, Check,
  CheckCircle, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  Circle, Clock, Cloud, Settings, Columns2, Copy, Crosshair,
  Database, Download, Pencil, MoreVertical, AlertCircle,
  AlertTriangle, Eye, EyeOff, Facebook, FileText, FileAudio2,
  FileOutput, Film, Fingerprint, FolderOpen, FastForward,
  Globe, Headphones, History, Image, Inbox, Info, Instagram,
  Key, Layers, Link, Linkedin, List, Lock, Wand2, Mic,
  MousePointer, Music, Palette, Pause, PenLine, Play, Plus,
  Podcast, HelpCircle, RotateCcw, Bot, Search, Server,
  LogOut, Loader, LayoutGrid, X, XCircle, Trash2, Twitter,
  Upload, Users, VolumeX, Volume2, Youtube, RefreshCw,
  Rewind, MoreHorizontal
} from 'lucide-vue-next'

const iconMap = {
  // Navigation & arrows
  'arrow-left':       ArrowLeft,
  'arrow-right':      ArrowRight,
  'chevron-left':     ChevronLeft,
  'chevron-right':    ChevronRight,
  'chevron-down':     ChevronDown,
  'chevron-up':       ChevronUp,
  // Player controls
  'play':             Play,
  'pause':            Pause,
  'step-backward':    SkipBack,
  'step-forward':     SkipForward,
  'backward':         Rewind,
  'forward':          FastForward,
  // Status & feedback
  'check':            Check,
  'check-circle':     CheckCircle,
  'times':            X,
  'times-circle':     XCircle,
  'exclamation-circle':   AlertCircle,
  'exclamation-triangle': AlertTriangle,
  'info-circle':      Info,
  'question-circle':  HelpCircle,
  'spinner':          Loader,
  'circle':           Circle,
  // Actions
  'search':           Search,
  'edit':             Pencil,
  'pencil-alt':       PenLine,
  'copy':             Copy,
  'download':         Download,
  'upload':           Upload,
  'trash':            Trash2,
  'redo':             RotateCcw,
  'sync-alt':         RefreshCw,
  'link':             Link,
  'eye':              Eye,
  'eye-slash':        EyeOff,
  'plus':             Plus,
  'ellipsis-v':       MoreVertical,
  'ellipsis-h':       MoreHorizontal,
  'crosshairs':       Crosshair,
  'camera':           Camera,
  'magic':            Wand2,
  'sign-out-alt':     LogOut,
  // Content & media
  'image':            Image,
  'film':             Film,
  'file-alt':         FileText,
  'file-audio':       FileAudio2,
  'file-export':      FileOutput,
  'music':            Music,
  'headphones':       Headphones,
  'microphone':       Mic,
  'podcast':          Podcast,
  'folder-open':      FolderOpen,
  // Data & UI
  'chart-bar':        BarChart2,
  'chart-pie':        PieChart,
  'database':         Database,
  'server':           Server,
  'layer-group':      Layers,
  'layers':           Layers,
  'th-large':         LayoutGrid,
  'list':             List,
  'columns':          Columns2,
  'history':          History,
  'clock':            Clock,
  'calendar-day':     CalendarDays,
  'cloud':            Cloud,
  'cog':              Settings,
  'key':              Key,
  'lock':             Lock,
  'robot':            Bot,
  'palette':          Palette,
  'bell':             Bell,
  'inbox':            Inbox,
  'mouse-pointer':    MousePointer,
  'fingerprint':      Fingerprint,
  'globe':            Globe,
  'users':            Users,
  'broadcast-tower':  Radio,
  // Volume
  'volume-up':        Volume2,
  'volume-mute':      VolumeX,
  // Brand icons
  'apple':            Apple,
  'facebook':         Facebook,
  'twitter':          Twitter,
  'instagram':        Instagram,
  'linkedin':         Linkedin,
  'youtube':          Youtube,
}

export default {
  name: 'AppIcon',
  props: {
    name: { type: String, required: true },
    size: { type: [Number, String], default: 16 },
    strokeWidth: { type: [Number, String], default: 1.75 },
    spin: { type: Boolean, default: false }
  },
  computed: {
    iconComponent() {
      return iconMap[this.name] || AlertCircle
    }
  }
}
</script>

<style>
.app-icon {
  display: inline-block;
  line-height: 0;
  vertical-align: middle;
  flex-shrink: 0;
  position: relative;
  top: -0.05em;
}
.app-icon svg {
  display: block;
}
.app-icon-spin {
  animation: app-icon-spin 0.8s linear infinite;
}
@keyframes app-icon-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* Buttons/anchors with icons — flex for alignment */
button:has(.app-icon),
a:has(.app-icon),
label:has(.app-icon) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
</style>
